import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CaFormFieldComponent, CaHint, CaIcon, CaLabel } from '@global-front-components/ui/form-field';
import { CaAccordionComponent, CaAccordionItemComponent } from '@global-front-components/ui/accordion';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CaFilterPipe } from '@global-front-components/common/filter-pipe';
import { CaDialogService } from '@global-front-components/ui/dialog';
import { CaSortByPipe } from '@global-front-components/common/sort-pipe';
import { CaInputDirective } from '@global-front-components/ui/input';
import { CaButtonComponent } from '@global-front-components/ui/button';
import { CaDatepickerComponent } from '@global-front-components/ui/datepicker';
import { CaSelectComponent } from '@global-front-components/ui/select';
import { SolicitudStoreService } from '../../services/store/solicitud-store.service';
import { forkJoin } from 'rxjs';
import { ColumnasTabla } from '../../../ui/model/tabla-seguros.model';
import { ActivatedRoute, Router } from '@angular/router';
import { BuscarSolicitud,Solicitud } from '../../model/solicitud.model';
import { CaTablaSegurosComponent } from "../../../ui/components/tabla-seguros/tabla-seguros.component";
import { AuthStoreService } from '../../services/store/auth-store.service';
import { EstadoSolicitud, RegistroSolicitud } from './model/buscador-polizas.model';
import { DateUtilsService } from '../../services/util/date-utils.service';

@Component({
	selector: 'app-buscador-polizas',
	standalone: true,
	imports: [
    CommonModule,
    CaFormFieldComponent,
    CaAccordionComponent,
    CaAccordionItemComponent,
    CaLabel,
    CaInputDirective,
    CaButtonComponent,
    CaHint,
    CaDatepickerComponent,
    ReactiveFormsModule,
    CaIcon,
    CaSelectComponent,
    TranslateModule,
    CaTablaSegurosComponent
],
	providers: [CaFilterPipe, CaDialogService, CaSortByPipe],
	templateUrl: './buscador-polizas.component.html',
	styleUrls: ['./buscador-polizas.component.scss']
})
export class BuscadorPolizasComponent implements OnInit, OnDestroy {
	public buscarForm: FormGroup;
	public registros: RegistroSolicitud[];
	public registroSeleccionado: RegistroSolicitud[];
	public solicitudes: Solicitud[] = [];
	public solicitudSeleccionada: Solicitud;
	public busqueda: BuscarSolicitud;
	columnas: ColumnasTabla[] = [];

	toggAcBusqueda = true;
	toggAcResultado = false;
	estados: EstadoSolicitud[] = [];

	iconTextAccordion = 'keyboard_arrow_down';

	constructor(
		private formBuilder: FormBuilder,
		private translateService: TranslateService,
		private solicitudStore: SolicitudStoreService,
		private authService: AuthStoreService,
		private router: Router,
		private route: ActivatedRoute,		
		private dateUtil: DateUtilsService	
	) { }

	private buildBuscarForm(): void {
		this.buscarForm = this.formBuilder.group({
			nPoliza: [this.busqueda?.numeroPoliza || ''],
			nColectiva: [this.busqueda?.codigoColectiva || ''],
			documento: [this.busqueda?.documento || ''],
			fEmiDesde: [this.busqueda?.fechaEmisionDesde || Date.now()],
			fEmiHasta: [this.busqueda?.fechaEmisionHasta || Date.now()],
			estado: [this.busqueda?.estado || ''],
		});
	}

	ngOnInit(): void {
		this.getTraducciones();

		this.solicitudStore.solicitudes$.subscribe((data) => {
			this.solicitudes = data;
			if (data && data.length > 0) {
				this.registros = this.convertirRegistroSolicitudes(this.solicitudes);				
				this.toggAcBusqueda = false;
				this.toggAcResultado = true;
			} else {
				this.registros=[];
				this.toggAcBusqueda = true;
				this.toggAcResultado = false;
			}
		});
		this.solicitudStore.busqueda$.subscribe((data) => {
			this.busqueda = data;
		});
		this.buildBuscarForm();
	}

	ngOnDestroy(): void {
		this.buscarForm?.reset(); 
	}

	private getTraducciones(): void {
		forkJoin([
			this.translateService.get('POLIZA.ESTADO.PENDIENTE'),
			this.translateService.get('POLIZA.ESTADO.RECHAZADO'),
			this.translateService.get('BUSCADOR_POLIZAS.TABLA_SOLICITUDES.COLECTIVA'),
			this.translateService.get('BUSCADOR_POLIZAS.TABLA_SOLICITUDES.CERTIFICADO'),
			this.translateService.get('BUSCADOR_POLIZAS.TABLA_SOLICITUDES.DOCUMENTO'),
			this.translateService.get('BUSCADOR_POLIZAS.TABLA_SOLICITUDES.FECHA_EMISION'),
			this.translateService.get('BUSCADOR_POLIZAS.TABLA_SOLICITUDES.ESTADO'),
			this.translateService.get('BUSCADOR_POLIZAS.TABLA_SOLICITUDES.MOVIMIENTO')
		]).subscribe(translations => {
			const estadoPendiente : EstadoSolicitud = {codEstado: "01", descEstado: translations[0]};
			const estadoRechazado : EstadoSolicitud = {codEstado: "00", descEstado: translations[1]};

			this.estados.push(estadoPendiente, estadoRechazado);
			this.columnas = [
				{ id: 0, field: 'codigoColectiva', value: translations[2] },
				{ id: 1, field: 'codigoCertificado', value: translations[3] },
				{ id: 2, field: 'documento', value: translations[4] },
				{ id: 3, field: 'fechaEmision', value: translations[5] },
				{ id: 4, field: 'descEstado', value: translations[6] },
				{ id: 5, field: 'numeroMovimiento', value: translations[7] },
			];
		});
	}
	
	seleccionarRegistro(registro: RegistroSolicitud) {
		this.solicitudSeleccionada = this.solicitudes.find(solicitud =>
			solicitud.codigoColectiva === registro.codigoColectiva &&
			solicitud.codigoCertificado === registro.codigoCertificado
		) || null;
	}

	buscar(): void {
		this.solicitudStore.limpiarSolicitudes();
		this.solicitudStore.limpiarBusqueda(); 
		this.solicitudSeleccionada = null;
		const nombreAgente = this.authService.getUsuarioActivo();
		const busqueda: BuscarSolicitud = {
			numeroPoliza: this.buscarForm.value.nPoliza,
			codigoColectiva: this.buscarForm.value.nColectiva,
			documento: this.buscarForm.value.documento,
			fechaEmisionDesde: this.buscarForm.value.fEmiDesde,
			fechaEmisionHasta: this.buscarForm.value.fEmiHasta,
			estado: this.buscarForm.value.estado,
			nombreAgente: nombreAgente
		};

		this.solicitudStore.getSolicitudes(busqueda);
	}

	verDetallePoliza() {
		if (this.solicitudSeleccionada) {
			this.router.navigate(['datos-solicitud'], {
				relativeTo: this.route,
				queryParams: {
					colectiva: this.solicitudSeleccionada.codigoColectiva,
					certificado: this.solicitudSeleccionada.codigoCertificado,
					estado: this.solicitudSeleccionada.codigoEstado,
					numMovimiento: this.solicitudSeleccionada.numeroMovimiento,
					tipoSolicitud: this.solicitudSeleccionada.tipoSolicitud
				}
			});				  
		}
	}

	toggleAccordion(event: Event): void {
		if (event) {
			this.iconTextAccordion = 'keyboard_arrow_up';
		} else {
			this.iconTextAccordion = 'keyboard_arrow_down';
		}
	}

	private convertirRegistroSolicitudes(solicitudes: Solicitud[]): RegistroSolicitud[] {
		return solicitudes.map(solicitud => ({
			codigoColectiva: solicitud.codigoColectiva,
			codigoCertificado: solicitud.codigoCertificado,
			documento: solicitud.tomador?.documento ?? '',
			fechaEmision: this.dateUtil.formatearFecha(solicitud.fechaEmision),
			descEstado: solicitud.descEstado,
			numeroMovimiento: solicitud.numeroMovimiento
		}));
	}
}
